import React, { useState } from 'react';
import { ScrollView, View, TouchableOpacity, Image, Modal } from 'react-native';
import { translate } from '../../../services/translate';
import MyAppText from '../../MyAppText';
import { faChevronDown, faChevronUp, faLocationDot, faSearchPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faCalendarCheck } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { Hoverable } from 'react-native-web-hover';
import { WindowInformation } from '../../../services/window-information';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { InfoInRow } from '../../InfoInRow';
import { isMappedSigrcPhoneOccurrence } from '../../../utils/getSigrcOccurrenceType';
import { MyImage } from '../../MyImage';

export default function OccurrenceFormSigrc({ occurrence }: { occurrence: Occurrence; }) {
    const { styles, theme } = useStyles(stylesheet);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const windowInfo = WindowInformation();
    const [showDetails, setShowDetails] = useState<boolean>(false);

    if (!occurrence.resume || occurrence.triggerType !== 'sigrc') {
        return <></>;
    }

    const resume = occurrence.resume as MappedSigrcPhoneOccurrence | SigRcResumeData;

    function addressFormat(address: { logradouro: string; numero: string; bairro: string; cidade: string; uf: string; cep: string; pontoReferencia?: string | null; }) {
        const formattedCEP = address.cep.replace(/(\d{5})(\d{3})/, '$1-$2');
        let formattedAddress = `${address.logradouro}, ${address.numero} - ${address.bairro}, ${address.cidade} - ${address.uf}, ${formattedCEP}`;
        if (address.pontoReferencia) {
            formattedAddress += ` (${formattedAddress})`;
        }
        return formattedAddress;
    }

    return (
        <>
            <ScrollView contentContainerStyle={{ rowGap: 10 }}>
                {(occurrence.resume as SigRcResumeData).imageUrl ? <Hoverable>
                    {({ hovered }) => (<>
                        <View style={{ flexDirection: 'column' }} >
                            <MyAppText style={styles.imageText}>
                                {translate('detection')}
                            </MyAppText>
                            <View style={{ alignItems: 'center' }}>
                                <Image
                                    style={{
                                        width: 180,
                                        height: 180,
                                        resizeMode: 'cover',
                                        borderRadius: 5,
                                        overflow: 'hidden'
                                    }}
                                    source={{ uri: `${(occurrence.resume as SigRcResumeData).imageUrl}` }}
                                />
                            </View>
                        </View>
                        {hovered &&
                            <View style={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                backgroundColor: '#00000080',
                            }}>
                                <TouchableOpacity
                                    style={{
                                        flex: 1,
                                        justifyContent: 'center'
                                    }}
                                    onPress={async () => {
                                        setIsModalVisible(true);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faSearchPlus} fontSize={28} color={'#FFFFFF'} />
                                </TouchableOpacity>
                            </View>
                        }
                    </>)}
                </Hoverable> : <></>}

                <View style={styles.generalInformation}>
                    <TouchableOpacity
                        style={styles.generalInformationDropdown}
                        onPress={() => setShowDetails(!showDetails)}>
                        <MyAppText>{translate('general_info')}</MyAppText>
                        <FontAwesomeIcon icon={showDetails ? faChevronUp : faChevronDown} color={theme.colors.iconColor} />
                    </TouchableOpacity>
                    <View style={styles.tableLine}></View>
                </View>

                {showDetails ? <>

                    {isMappedSigrcPhoneOccurrence(resume) ? (
                        <>
                            <View style={{ rowGap: 5 }}>
                                <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 5 }}>
                                    <FontAwesomeIcon icon={faCalendarCheck} style={{ width: 22 }} color={theme.colors.iconColor} />
                                    <MyAppText style={styles.detectionInfoText}>
                                        {moment(occurrence.createdAt).format('DD/MM/YYYY HH:mm:ss')}
                                    </MyAppText>
                                </View>
                                <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 5 }}>
                                    <FontAwesomeIcon icon={faLocationDot} style={{ width: 22 }} color={theme.colors.iconColor} />
                                    <MyAppText style={styles.detectionInfoText}>
                                        {addressFormat(resume.content.endereco)}
                                    </MyAppText>
                                </View>
                            </View>
                            <View style={{ rowGap: 5 }}>
                                <InfoInRow value={resume.sigrcId} title='sigrcId' />
                                <InfoInRow value={resume.externalProtocol} title='externalProtocol' />
                                <InfoInRow value={resume.content.tipoSolicitacao} title='type' />
                                <InfoInRow value={resume.content.descricao} title='description' />
                                <InfoInRow value={resume.content.endereco.prefeitura} title='subprefecture' />

                                <InfoInRow value={resume.content.dataCadastro} title='registeredAt' isDate={true} />
                                <InfoInRow value={resume.content.criticidade} title='criticality' />

                                {resume.requester ?
                                    <View style={{ rowGap: 1 }}>
                                        <MyAppText style={styles.requesterTitle}>
                                            {translate('requester')}
                                        </MyAppText>

                                        <InfoInRow value={resume.requester.nome} title='name' />
                                        {resume.requester.cpf
                                            ? <InfoInRow value={resume.requester.cpf} title='document' />
                                            : <InfoInRow value={resume.requester.cnpj ?? ''} title='document' />}
                                        {resume.requester?.telefone?.length > 0 ? (
                                            resume.requester.telefone.map((tel) => (
                                                <InfoInRow key={tel.id} value={`(${tel.ddd}) ${tel.numero}`} title='phone' />
                                            ))
                                        ) : null}

                                    </View>
                                    : <></>
                                }


                                <View style={{ rowGap: 1 }}>
                                    <MyAppText style={styles.requesterTitle}>
                                        {translate('systemData')}
                                    </MyAppText>

                                    <InfoInRow value={resume.occurrenceSize} title='occurrenceSize' />
                                    <InfoInRow value={resume.occurrenceDate} title='occurrenceDate' />
                                    <InfoInRow value={resume.victimsCount} title='victimsCount' />
                                    <InfoInRow value={resume.vehiclesCount} title='vehiclesCount' />
                                    <InfoInRow value={resume.propertyType} title='propertyType' />
                                    <InfoInRow value={resume.residencesCount} title='residencesCount' />
                                    <InfoInRow value={resume.buildingsCount} title='buildingsCount' />
                                    <InfoInRow value={resume.victimsDeficientCount} title='victimsDeficientCount' />
                                    <InfoInRow value={resume.isPoliceVehicleInPlace} title='isPoliceVehicleInPlace' />
                                    <InfoInRow value={resume.homelessCount} title='homelessCount' />
                                    <InfoInRow value={resume.blockedPassage} title='blockedPassage' />
                                    <InfoInRow value={resume.specificLocation} title='specificLocation' />
                                    <InfoInRow value={resume.vehicleInjured} title='vehicleInjured' />
                                    <InfoInRow value={resume.propertyInjured} title='propertyInjured' />
                                    <InfoInRow value={resume.victimsDescription} title='victimsDescription' />
                                </View>


                                {Array.isArray(resume.victims) && resume.victims.length > 0 && <View style={{ rowGap: 1 }}>
                                    <MyAppText style={styles.requesterTitle}>
                                        {translate('registeredVictims')}
                                    </MyAppText>

                                    {resume.victims && resume.victims.map((victim, index) => (
                                        <div style={{ rowGap: 1, display: 'flex', flexDirection: 'column' }} key={victim.id}>
                                            <InfoInRow value={victim.estado} title='victimState' titleSuffix={String(index + 1)} />
                                            <InfoInRow value={victim.sexo} title='victimSex' titleSuffix={String(index + 1)} />
                                        </div>
                                    ))}
                                </View>}
                            </View>
                        </>
                    ) : (
                        <>
                            <View style={{ rowGap: 5 }}>
                                <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 5 }}>
                                    <FontAwesomeIcon icon={faCalendarCheck} style={{ width: 22 }} color={theme.colors.iconColor} />
                                    <MyAppText style={styles.detectionInfoText}>
                                        {moment(occurrence.createdAt).format('DD/MM/YYYY HH:mm:ss')}
                                    </MyAppText>
                                </View>
                                <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 5 }}>
                                    <FontAwesomeIcon icon={faLocationDot} style={{ width: 22 }} color={theme.colors.iconColor} />
                                    <MyAppText style={styles.detectionInfoText}>
                                        {addressFormat(resume.content.endereco)}
                                    </MyAppText>
                                </View>
                            </View>
                            <View style={{ rowGap: 5 }}>
                                <InfoInRow value={resume.content.id} title='sigrcId' />
                                <InfoInRow value={resume.content.protocoloExterno} title='externalProtocol' />
                                <InfoInRow value={resume.content.tipoSolicitacao.descricao} title='type' />
                                <InfoInRow value={resume.content.descricao} title='description' />
                                <InfoInRow value={resume.content.endereco.prefeitura} title='subprefecture' />

                                {resume.content.tipoSolicitacao.sla ?
                                    <>
                                        <InfoInRow value={resume.content.tipoSolicitacao.sla.status} title='status' />
                                        <InfoInRow value={resume.content.tipoSolicitacao.sla.nome} title='completionTime' />
                                    </>
                                    : <></>
                                }

                                <InfoInRow value={resume.content.tipoSolicitacao.dataCadastro} title='registeredAt' isDate={true} />
                                <InfoInRow value={resume.content.tipoSolicitacao.criticidade} title='criticality' />

                                {resume.content.solicitante ?
                                    <View style={{ rowGap: 1 }}>
                                        <MyAppText style={styles.requesterTitle}>
                                            {translate('requester')}
                                        </MyAppText>

                                        <InfoInRow value={resume.content.solicitante?.nome} title='name' />
                                        <InfoInRow value={resume.content.solicitante?.cpfCNPJ} title='document' />
                                        {resume.content.solicitante?.telefone?.length > 0 ? (
                                            resume.content.solicitante.telefone.map((tel) => (
                                                <InfoInRow key={tel.id} value={`(${tel.ddd}) ${tel.numero}`} title='phone' />
                                            ))
                                        ) : null}

                                    </View>
                                    : <></>
                                }
                            </View>
                        </>
                    )}
                </> : <></>}
            </ScrollView >

            {/* Zoom image modal */}
            <Modal
                animationType='fade'
                transparent={true}
                visible={isModalVisible}
                onRequestClose={() => setIsModalVisible(false)}>
                <View style={styles.modalContainer}>
                    <View style={styles.centeredView}>
                        <View style={[styles.modalBody, windowInfo.isMobile ? { width: '95%' } : null]}>
                            <View style={{ alignItems: 'flex-end' }}>
                                <TouchableOpacity
                                    style={{ width: 20, height: 20 }}
                                    onPress={() => setIsModalVisible(false)}
                                >
                                    <FontAwesomeIcon icon={faTimes} style={{ width: 20 }} color={theme.colors.iconColor} />
                                </TouchableOpacity>
                            </View>

                            <View style={{ flexDirection: 'row', gap: 15 }} >
                                <View style={{ flex: 1 }}>
                                    <MyAppText style={styles.imageText}>
                                        {translate('detection')}
                                    </MyAppText>
                                    <MyImage imageUrl={(resume as SigRcResumeData).imageUrl ?? ''} />
                                </View>
                            </View>
                        </View>
                    </View>
                </View>
            </Modal>
        </>
    );
}

const stylesheet = createStyleSheet(theme => ({
    container: {
        padding: 16,
        backgroundColor: theme.colors.container.background,
        borderRadius: 8,
        borderColor: theme.colors.container.border,
        borderWidth: 1,
    },
    imageText: {
        color: theme.colors.card.detailText,
        paddingBottom: 5,
        fontSize: 11,
    },
    detectionInfoText: {
        color: theme.colors.labelColor,
        fontSize: 12
    },
    modalContainer: {
        flex: 1,
        backgroundColor: '#31313199',
    },
    centeredView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalBody: {
        backgroundColor: theme.colors.container.background,
        flexDirection: 'column',
        borderRadius: 8,
        rowGap: 20,
        padding: 30,
    },
    requesterTitle: {
        fontWeight: 'bold',
        fontSize: 15
    },
    tableLine: {
        borderBottomColor: theme.colors.card.border,
        borderBottomWidth: 1,
        flex: 1
    },
    generalInformation: {
        flexDirection: 'column',
        paddingHorizontal: 5,
        flex: 1,
        gap: 10
    },
    generalInformationDropdown: {
        flexDirection: 'row',
        gap: 10,
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: 1
    }
}));
